/** The event page's behavior half: reads the page's event, the studio's name and, on a custom site, its links. Draws nothing. */
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { SiteFetchError, useSiteEvent, useSiteStudio } from '../../api/site';
import { usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { getClientTimezone } from '../../format';
import { readSite } from '../../site';
import { contract, type EventPageActions, type EventPageEvents, type EventPageState } from './contract';

export type EventPageRuntime = PieceRuntime<typeof contract, EventPageState, EventPageActions>;

export interface EventPageInput extends PieceInput<EventPageEvents> {
  /** The event to show; defaults to the page address's event. */
  readonly eventId?: string;
}

const NO_ACTIONS: EventPageActions = {};

export function useEventPage(input: EventPageInput = {}): EventPageRuntime {
  const options = usePieceOptions(contract, input.options);
  const params = useParams<{ eventId?: string }>();
  const query = useSiteEvent(input.eventId ?? params.eventId);
  const studio = useSiteStudio();
  const timezone = useMemo(() => getClientTimezone(), []);

  const status: EventPageState['status'] = query.isError
    ? query.error instanceof SiteFetchError && query.error.status >= 400 && query.error.status < 500
      ? 'not-found'
      : 'error'
    : query.isPending
      ? 'loading'
      : 'ready';

  return {
    state: { status, event: query.data ?? null, studioName: studio.data?.name ?? null, siteLinks: readSite()?.links ?? null, timezone },
    actions: NO_ACTIONS,
    options,
  };
}
